import { useEffect, useState } from 'react';

/**
 * 해시 라우팅 (통합표준 4절)
 *
 * ONE 쉘은 iframe 을 새로고침하지 않고 `frame.contentWindow.location.hash` 만 바꿔서
 * 화면을 전환한다. 따라서 내부 네비게이션은 반드시 location.hash 를 반영해야 하고,
 * hashchange 를 듣고 있어야 한다.
 */

export type Route =
  | { name: 'dashboard' }
  | { name: 'projects' }
  | { name: 'project'; projectId: string }
  | { name: 'drawing'; drawingId: string }
  | { name: 'version'; drawingId: string; versionId: string }
  | { name: 'compare'; drawingId: string; a: string; b: string }
  | { name: 'blocks' }
  | { name: 'activity' }
  | { name: 'admin' }
  | { name: 'notfound'; raw: string };

export const DEFAULT_ROUTE: Route = { name: 'dashboard' };

function segments(hash: string): string[] {
  return hash
    .replace(/^#\/?/, '')
    .split('?')[0]!
    .split('/')
    .filter(Boolean)
    .map(decodeURIComponent);
}

export function parseHash(hash: string): Route {
  const s = segments(hash);
  if (s.length === 0) return DEFAULT_ROUTE;

  switch (s[0]) {
    case 'dashboard':
      return { name: 'dashboard' };
    case 'projects':
      return { name: 'projects' };
    case 'blocks':
      return { name: 'blocks' };
    case 'activity':
      return { name: 'activity' };
    case 'admin':
      return { name: 'admin' };
    case 'project':
      return s[1] ? { name: 'project', projectId: s[1] } : { name: 'projects' };
    case 'drawing': {
      const id = s[1];
      if (!id) return { name: 'notfound', raw: hash };
      if (s[2] === 'v' && s[3]) return { name: 'version', drawingId: id, versionId: s[3] };
      if (s[2] === 'compare' && s[3] && s[4]) {
        return { name: 'compare', drawingId: id, a: s[3], b: s[4] };
      }
      return { name: 'drawing', drawingId: id };
    }
    default:
      return { name: 'notfound', raw: hash };
  }
}

export function toHash(route: Route): string {
  const e = encodeURIComponent;
  switch (route.name) {
    case 'dashboard':
      return '#/dashboard';
    case 'projects':
      return '#/projects';
    case 'blocks':
      return '#/blocks';
    case 'activity':
      return '#/activity';
    case 'admin':
      return '#/admin';
    case 'project':
      return `#/project/${e(route.projectId)}`;
    case 'drawing':
      return `#/drawing/${e(route.drawingId)}`;
    case 'version':
      return `#/drawing/${e(route.drawingId)}/v/${e(route.versionId)}`;
    case 'compare':
      return `#/drawing/${e(route.drawingId)}/compare/${e(route.a)}/${e(route.b)}`;
    case 'notfound':
      return route.raw || '#/dashboard';
  }
}

/* ------------------------------------------------------------------ *
 * 이탈 가드 — 편집기에서 미저장 변경이 있을 때 이동을 막는다.
 * 쉘이 hash 만 바꿔 전환하는 경우에도 동작해야 하므로 hashchange 를 가로챈다.
 * ------------------------------------------------------------------ */

type LeaveGuard = () => boolean | Promise<boolean>;
let leaveGuard: LeaveGuard | null = null;
let reverting = false;

export function setLeaveGuard(fn: LeaveGuard | null): void {
  leaveGuard = fn;
}

/** 현재 해시를 읽어 라우트를 반환. */
export function currentRoute(): Route {
  return parseHash(window.location.hash);
}

/** 내부 네비게이션. 해시가 이미 같으면 아무것도 하지 않는다. */
export function navigate(route: Route, replace = false): void {
  const next = toHash(route);
  if (window.location.hash === next) return;
  if (replace) {
    const url = window.location.href.split('#')[0] + next;
    window.history.replaceState(null, '', url);
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  } else {
    window.location.hash = next;
  }
}

/**
 * 라우트 구독. 부팅(세션 확인 + 데이터 로드) 완료 직후 마운트되어야
 * 딥링크(#/drawing/xxx 등)로 들어온 경우가 정상 처리된다.
 */
export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => {
    // 해시가 비어 있으면 기본 경로를 심어둔다 (쉘이 나중에 hash 만 바꿀 수 있도록).
    if (!window.location.hash) {
      window.history.replaceState(null, '', window.location.href.split('#')[0] + '#/dashboard');
    }
    return currentRoute();
  });

  useEffect(() => {
    let lastHash = window.location.hash;

    const onHashChange = () => {
      if (reverting) {
        reverting = false;
        lastHash = window.location.hash;
        return;
      }

      const from = lastHash;
      const to = window.location.hash;

      const commit = () => {
        lastHash = to;
        setRoute(parseHash(to));
      };

      if (!leaveGuard) {
        commit();
        return;
      }

      void Promise.resolve(leaveGuard()).then((ok) => {
        if (ok) {
          commit();
        } else {
          // 되돌리기. 이때 발생하는 hashchange 는 무시한다.
          reverting = true;
          window.location.hash = from;
        }
      });
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return route;
}
